import React from "react";
import { Link, useLocation } from "react-router-dom";
import { LayoutDashboard, Inbox, BookOpen, ShoppingBag, MessageSquare, LineChart, Megaphone, Building, Settings, LogOut } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

/**
 * Vendor-side shell — sidebar with 9 menu items per Build Guide Phase 5 spec
 * (Dashboard, Requests, Catalog, Orders, Messages, Analytics, Marketing,
 * Company Profile, Settings) + top bar showing the vendor's company name.
 */
export default function VendorLayout({ children }) {
  const { user, logout } = useAuth();
  const { pathname } = useLocation();

  const items = [
    { to: "/vendor", icon: <LayoutDashboard size={17} />, label: "Dashboard" },
    { to: "/vendor/requests", icon: <Inbox size={17} />, label: "Requests" },
    { to: "/vendor/catalogs", icon: <BookOpen size={17} />, label: "Catalog" },
    { to: "/vendor/orders", icon: <ShoppingBag size={17} />, label: "Orders" },
    { to: "/vendor/messages", icon: <MessageSquare size={17} />, label: "Messages" },
    { to: "/vendor/analytics", icon: <LineChart size={17} />, label: "Analytics" },
    { to: "/vendor/marketing", icon: <Megaphone size={17} />, label: "Marketing" },
    { to: "/vendor/profile", icon: <Building size={17} />, label: "Company Profile" },
    { to: "/vendor/settings", icon: <Settings size={17} />, label: "Settings" },
  ];

  return (
    <div className="min-h-screen bg-[#F9F8F4] flex" data-testid="vendor-layout">
      {/* Sidebar */}
      <aside className="hidden lg:flex w-64 flex-col bg-[#1A422D] text-[#F9F8F4] min-h-screen">
        <div className="px-6 py-8 border-b border-[#F9F8F4]/10">
          <div className="flex items-center gap-3">
            <img src="/logo.png" alt="MCC" className="h-9 w-auto" />
            <div>
              <div className="text-[10px] uppercase tracking-[0.28em] font-semibold text-[#D9C68A]">
                Vendor Portal
              </div>
              <div className="font-serif text-lg leading-tight mt-0.5">My Crest Club</div>
            </div>
          </div>
        </div>

        <nav className="flex-1 py-4">
          {items.map((it) => {
            const active = pathname === it.to;
            return (
              <Link
                key={it.to}
                to={it.to}
                data-testid={`vendor-nav-${it.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                className={`flex items-center gap-3 px-6 py-3 text-[12px] uppercase tracking-[0.22em] font-semibold transition-colors ${
                  active
                    ? "bg-[#0F2A1C] text-[#D9C68A] border-l-2 border-[#D9C68A]"
                    : "text-[#F9F8F4]/85 hover:bg-[#0F2A1C] hover:text-[#D9C68A]"
                }`}
              >
                {it.icon}
                {it.label}
              </Link>
            );
          })}
        </nav>

        <button
          type="button"
          data-testid="vendor-logout"
          onClick={logout}
          className="mx-6 mb-6 mt-4 flex items-center gap-2 py-2.5 px-4 border border-[#F9F8F4]/20 text-[11px] uppercase tracking-[0.22em] font-semibold text-[#F9F8F4]/85 hover:text-[#D9C68A] hover:border-[#D9C68A] transition-colors"
        >
          <LogOut size={14} /> Sign Out
        </button>
      </aside>

      {/* Content */}
      <div className="flex-1 min-w-0">
        {/* Top bar */}
        <header className="bg-white border-b border-[#E5E3D8] px-6 sm:px-10 py-5 flex items-center justify-between">
          <div>
            <div className="text-[11px] uppercase tracking-[0.24em] font-semibold text-[#B59A5A]">
              Signed in as
            </div>
            <div className="font-serif text-xl text-[#1A422D] mt-0.5" data-testid="vendor-company-name">
              {user?.company_name || user?.name || "Vendor"}
            </div>
          </div>
          <div className="text-xs uppercase tracking-[0.22em] text-[#7A7A7A]">
            Vendor
          </div>
        </header>

        <main className="p-6 sm:p-10">{children}</main>
      </div>
    </div>
  );
}
